import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';

// Cards tab component
export const CardsTab = ({
  cards,
  decks,
  cardTypes,
  users,
  languages,
  onAddCard,
  onEditCard,
  showAddForm,
  setShowAddForm,
  editingCard,
  setEditingCard,
  onImportComplete,
  symbols = [],
  onRowClick
}) => {
  const [filteredCards, setFilteredCards] = useState(cards);
  const [searchTerm, setSearchTerm] = useState("");
  const [deckFilter, setDeckFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const cardsPerPage = 20;
  const { user } = useAuth();
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    let result = [...cards];
    if (deckFilter) {
      result = result.filter(card => card.deck_id === parseInt(deckFilter));
    }
    if (typeFilter) {
      result = result.filter(card => card.type_id === parseInt(typeFilter));
    }
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(card => {
        return (
          card.card_id.toString().includes(searchLower) ||
          (card.deck_translations?.en?.name || '').toLowerCase().includes(searchLower) ||
          (card.type_name || '').toLowerCase().includes(searchLower)
        );
      });
    }
    setFilteredCards(result);
    setCurrentPage(1);
  }, [cards, deckFilter, typeFilter, searchTerm]);

  // Fetch full card details when editingCard is set
  useEffect(() => {
    if (editingCard && editingCard.card_id && !editingCard.texts) {
      const fetchCard = async () => {
        try {
          const res = await api.get(`/api/admin/cards/${editingCard.card_id}`);
          setEditingCard(res.data);
        } catch (err) {
          setEditingCard(editingCard); // fallback
        }
      };
      fetchCard();
    }
  }, [editingCard]);

  const indexOfLastCard = currentPage * cardsPerPage;
  const indexOfFirstCard = indexOfLastCard - cardsPerPage;
  const currentCards = filteredCards.slice(indexOfFirstCard, indexOfLastCard);
  const totalPages = Math.ceil(filteredCards.length / cardsPerPage);

  // Add this function to handle card updates
  const handleEditCardSave = async (cardId, cardData) => {
    await api.put(`/api/admin/cards/${cardId}`, cardData);
    setEditingCard(null); // Close the form after save
    // Optionally, refresh cards list here if needed
  };

  return (
      <div>
        <h2>Cards</h2>

        <div style={{ marginBottom: "20px", display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button className="small-action-btn add" onClick={() => onRowClick && onRowClick('cards', { card_id: -1, isNew: true })}>
            Add Card
          </button>
          <input
            type="text"
            placeholder="Search by card ID, deck, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginLeft: 10, marginRight: "10px", padding: "5px" }}
          />
          <select
            value={deckFilter}
            onChange={(e) => setDeckFilter(e.target.value)}
            style={{ marginRight: "10px", padding: "5px" }}
          >
            <option value="">All Decks</option>
            {decks.sort((a, b) => (a.translations?.en?.name || `Deck ${a.deck_id}`).localeCompare(b.translations?.en?.name || `Deck ${b.deck_id}`)).map(deck => (
              <option key={deck.deck_id} value={deck.deck_id}>
                {deck.translations?.en?.name || `Deck ${deck.deck_id}`}
              </option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{ padding: "5px" }}
          >
            <option value="">All Types</option>
            {cardTypes.sort((a, b) => (a.translations?.en?.name || a.name || a.type_id).localeCompare(b.translations?.en?.name || b.name || b.type_id)).map(type => (
              <option key={type.type_id} value={type.type_id}>
                {type.translations?.en?.name || type.name || type.type_id}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: "10px" }}>
          Showing {indexOfFirstCard + 1}-{Math.min(indexOfLastCard, filteredCards.length)} of {filteredCards.length} cards
        </div>
        <button onClick={() => document.getElementById('excel-import-input').click()} style={{ marginBottom: 10 }}>Import Excel</button>
        {importing && (
          <div style={{ margin: '10px 0', width: 300 }}>
            <div style={{
              height: 16,
              background: 'var(--surface)',
              borderRadius: 8,
              overflow: 'hidden',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                width: `${importProgress}%`,
                height: '100%',
                background: 'var(--primary)',
                transition: 'width 0.2s'
              }} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text)', marginTop: 2 }}>{importProgress}%</div>
          </div>
        )}
        <input
          id="excel-import-input"
          type="file"
          accept=".xlsx, .xls"
          style={{ display: 'none' }}
          onChange={async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            setImporting(true);
            setImportProgress(0);
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            // First row is header
            let [header, ...rows] = json;
            // Find option columns
            const optionCols = header.filter(h => /^option\d+_en$/.test(h));
            const cardIdColIdx = header.findIndex(h => h === 'card_id');
            let success = 0, fail = 0;
            let errorDetails = [];
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const newRows = [];
            for (let i = 0; i < rows.length; i++) {
              const row = rows[i];
              if (!row.length) continue;
              const rowObj = Object.fromEntries(header.map((h, idx) => [h, row[idx]]));
              const texts = optionCols.map((col, idx) => ({
                option_index: idx + 1,
                language: 'en',
                text: (rowObj[col] || '').toLowerCase()
              })).filter(t => t.text && t.text.trim());
              if (!texts.length) {
                newRows.push(row);
                continue;
              }
              const cardData = {
                deck_id: rowObj.deck_id,
                type_id: rowObj.type_id,
                custom_background: null,
                texts,
                created_by: user?.user_id
              };
              let newCardId = rowObj.card_id;
              try {
                if (rowObj.card_id) {
                  // Try update first
                  try {
                    await api.put(`/api/admin/cards/${rowObj.card_id}`,
                      {
                        deck_id: cardData.deck_id,
                        type_id: cardData.type_id,
                        custom_background: cardData.custom_background,
                        texts: cardData.texts
                      }
                    );
                    success++;
                  } catch (err) {
                    // If not found, create new card
                    if (err?.response?.status === 404) {
                      const res = await api.post('/api/admin/cards', cardData);
                      newCardId = res.data.card_id;
                      row[cardIdColIdx] = newCardId;
                      success++;
                    } else {
                      fail++;
                      errorDetails.push({ card: cardData, error: err?.response?.data?.error || err.message || err.toString() });
                      console.error('Import error:', err, cardData);
                    }
                  }
                } else {
                  // Always create new card
                  const res = await api.post('/api/admin/cards', cardData);
                  newCardId = res.data.card_id;
                  row[cardIdColIdx] = newCardId;
                  success++;
                }
              } catch (err) {
                fail++;
                errorDetails.push({ card: cardData, error: err?.response?.data?.error || err.message || err.toString() });
                console.error('Import error:', err, cardData);
              }
              newRows.push(row);
              setImportProgress(Math.round(((i + 1) / rows.length) * 100));
            }
            setImporting(false);
            setImportProgress(0);
            let message = `Import complete. Success: ${success}, Failed: ${fail}`;
            if (fail > 0) {
              message += '\n\nFailed cards:';
              errorDetails.forEach((detail, i) => {
                message += `\n${i + 1}. Card: ${JSON.stringify(detail.card)}\n   Error: ${detail.error}`;
              });
            }
            if (fail > 0) {
              errorDetails.forEach((detail, i) => {
                console.error(`Import failed for card #${i + 1}:`, detail.card, '\nError:', detail.error);
              });
            }
            if (fail > 0 && errorDetails.length > 0) {
              alert(message); // or use your toast/notification system
            }
            // Generate new Excel file with updated card_id column and trigger download
            const outSheet = XLSX.utils.aoa_to_sheet([header, ...newRows]);
            const outWb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(outWb, outSheet, 'ImportedCards');
            const outFile = XLSX.write(outWb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([outFile], { type: 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'imported_cards_with_ids.xlsx';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }, 100);
            if (typeof onImportComplete === 'function') onImportComplete();
            document.getElementById('excel-import-input').value = '';
          }}
        />
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Deck</th>
              <th>Type</th>
              <th>Background</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCards.map((card) => (
              <tr key={card.card_id} onClick={() => onRowClick && onRowClick('cards', card)} style={{ cursor: 'pointer' }}>
                <td>{card.card_id}</td>
                <td>{card.deck_translations?.en?.name || card.deck_name || card.deck_id}</td>
                <td>{card.type_name || card.type_id}</td>
                <td>{card.custom_background ? <img src={card.custom_background} alt="Background" style={{ width: 50, height: 50, objectFit: 'cover' }} /> : <span style={{ color: '#aaa' }}>No image</span>}</td>
                <td>
                                  <button className="small-action-btn edit" onClick={(e) => { e.stopPropagation(); setShowAddForm(false); setEditingCard(card); }}>Edit</button>
                                  <button className="small-action-btn delete" onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Are you sure you want to delete this card?`)) {
                                      onEditCard(card.card_id, null, true);
                                    }
                                  }}>Delete</button>
                                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div style={{ marginTop: "20px" }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span style={{ margin: "0 10px" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

// Add Card Form
export function AddCardForm({ decks, cardTypes, onAdd, onCancel, languages = [], symbols = [] }) {
  const [deckId, setDeckId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [customBackground, setCustomBackground] = useState("");
  const [texts, setTexts] = useState([{ option_index: 1, translations: {} }]);
  const [cardSymbolsState, setCardSymbolsState] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();

  const handleAddText = () => {
    setTexts([...texts, { option_index: texts.length + 1, translations: {} }]);
  };
  const handleTextChange = (textIdx, langCode, value) => {
    setTexts(texts.map((t, i) =>
      i === textIdx ? { ...t, translations: { ...t.translations, [langCode]: value } } : t
    ));
  };
  const handleAddSymbol = () => {
    setCardSymbolsState([...cardSymbolsState, { symbol_index: cardSymbolsState.length + 1, symbol_path: "" }]);
  };
  const handleSymbolChange = (idx, value) => {
    setCardSymbolsState(cardSymbolsState.map((s, i) => i === idx ? { ...s, symbol_path: value } : s));
  };
  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post('/api/admin/cards/upload-image', formData);
      setCustomBackground(res.data.fileUrl);
    } catch (err) {
      setError("Failed to upload image");
    }
    setUploading(false);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!deckId || !typeId) {
      setError("Deck and type are required");
      return;
    }
    setUploading(true);
    setError("");
    try {
      // Flatten texts for backend: [{ option_index, language, text }]
      const flatTexts = [];
      texts.forEach((t, idx) => {
        Object.entries(t.translations).forEach(([lang, text]) => {
          if (text && text.trim()) {
            flatTexts.push({ option_index: idx + 1, language: lang, text: text.trim() });
          }
        });
      });
      await onAdd({
        deck_id: parseInt(deckId),
        type_id: parseInt(typeId),
        custom_background: customBackground,
        created_by: user?.user_id,
        texts: flatTexts,
        symbols: cardSymbolsState
      });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to add card");
    }
    setUploading(false);
  };
  // Helper to insert symbol reference at cursor
  const insertSymbolReference = (textIdx, langCode, symbolId) => {
    const inputId = `addcard-text-input-${textIdx}-${langCode}`;
    const input = document.getElementById(inputId);
    if (!input) return;
    const value = input.value;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const ref = `{{symbol:${symbolId}}}`;
    const newValue = value.slice(0, start) + ref + value.slice(end);
    handleTextChange(textIdx, langCode, newValue);
    // Restore cursor position after update
    setTimeout(() => {
      input.focus();
      input.selectionStart = input.selectionEnd = start + ref.length;
    }, 0);
  };
  // Only show symbols of type 'Card type' or 'Symbol' from the symbols prop
  const cardSymbols = symbols.filter(symbol => symbol.symbol_type === 'Card type' || symbol.symbol_type === 'Symbol');
  return (
    <div style={{ width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ marginBottom: "20px", padding: "20px", border: "1px solid var(--border)", background: "var(--surface)", borderRadius: "20px", maxWidth: 900, minWidth: 600 }}>
        <h3>Add Card</h3>
        <div style={{ marginBottom: 10 }}>
          <label>Deck:</label><br />
          <select value={deckId} onChange={e => setDeckId(e.target.value)} required>
            <option value="">Select deck...</option>
            {decks.sort((a, b) => (a.translations?.en?.name || `Deck ${a.deck_id}`).localeCompare(b.translations?.en?.name || `Deck ${b.deck_id}`)).map(deck => (
              <option key={deck.deck_id} value={deck.deck_id}>{deck.translations?.en?.name || `Deck ${deck.deck_id}`}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label>Type:</label><br />
          <select value={typeId} onChange={e => setTypeId(e.target.value)} required>
            <option value="">Select Type</option>
            {cardTypes.sort((a, b) => (a.translations?.en?.name || a.name || a.type_id).localeCompare(b.translations?.en?.name || b.name || b.type_id)).map(type => (
              <option key={type.type_id} value={type.type_id}>
                {type.translations?.en?.name || type.name || type.type_id}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label>Custom Background (PNG):</label><br />
          <input type="file" accept="image/png" onChange={handleBackgroundUpload} />
          {customBackground && <img src={customBackground} alt="bg" style={{ width: 100, height: 100, objectFit: 'cover', marginLeft: 10 }} />}
        </div>

        <div style={{ marginBottom: 8, fontWeight: 500, color: '#357abd' }}>
          You can insert a symbol reference into any text option using the dropdown next to the input. The symbol will appear as <code>{'{{symbol:ID}}'}</code> in the text.
        </div>
        <div style={{ marginBottom: 10 }}>
          <label>Texts:</label>
          {languages.map(lang => (
            <div key={lang.code} style={{ marginBottom: 10, paddingLeft: 10, borderLeft: '2px solid #eee' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{lang.name} ({lang.code}):</div>
              {texts.map((t, idx) => (
                <div key={idx} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'inline-block', width: 80 }}>Option {idx + 1}:</label>
                  <input
                    type="text"
                    id={`addcard-text-input-${idx}-${lang.code}`}
                    value={t.translations?.[lang.code] || ''}
                    onChange={e => handleTextChange(idx, lang.code, e.target.value)}
                    placeholder={`Text in ${lang.name}`}
                    style={{ width: 350, marginRight: 10 }}
                  />
                  {cardSymbols.length > 0 && (
                    <select
                      style={{ minWidth: 180, fontWeight: 600, background: '#f8f9fa', border: '2px solid #4a90e2', color: '#222', padding: '6px 10px', borderRadius: 6 }}
                      onChange={e => {
                        if (e.target.value) {
                          insertSymbolReference(idx, lang.code, e.target.value);
                          e.target.selectedIndex = 0;
                        }
                      }}
                    >
                      <option value="">Insert Symbol...</option>
                      {cardSymbols.map(symbol => (
                        <option key={symbol.symbol_id} value={symbol.symbol_id}>
                          {symbol.translations?.en?.name || symbol.translations?.en || `Symbol ${symbol.symbol_id}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
          ))}
          <button type="button" onClick={handleAddText}>+</button>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label>Symbols:</label>
          {cardSymbolsState.map((s, idx) => (
            <div key={idx} style={{ marginBottom: 5 }}>
              <input type="text" value={s.symbol_path} onChange={e => handleSymbolChange(idx, e.target.value)} placeholder={`Symbol Path ${idx + 1}`} style={{ width: 300, marginRight: 10 }} />
              <button type="button" onClick={handleAddSymbol}>+</button>
            </div>
          ))}
        </div>
        {error && <div style={{ color: 'red' }}>{error}</div>}
        <button type="submit" disabled={uploading}>Add Card</button>
        <button type="button" onClick={onCancel} style={{ marginLeft: 10 }}>Cancel</button>
      </form>
    </div>
  );
}

// Edit Card Form
export function EditCardForm({ card, decks, cardTypes, onSave, onCancel, languages = [], symbols = [] }) {
  const [deckId, setDeckId] = useState(card.deck_id);
  const [typeId, setTypeId] = useState(card.type_id);
  const [customBackground, setCustomBackground] = useState(card.custom_background || "");
  const [texts, setTexts] = useState(card.texts && card.texts.length ? card.texts : [{ option_index: 1, translations: {} }]);
  const [cardSymbolsState, setCardSymbolsState] = useState(card.symbols || []);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    if (card.texts && card.texts.length) {
      setTexts(card.texts);
    }
  }, [card.texts]);

  const handleTextChange = (textIdx, langCode, value) => {
    setTexts(texts.map((t, i) =>
      i === textIdx ? { ...t, translations: { ...t.translations, [langCode]: value } } : t
    ));
  };
  const handleAddText = () => {
    setTexts([...texts, { option_index: texts.length + 1, translations: {} }]);
  };
  const handleSymbolChange = (idx, value) => {
    setCardSymbolsState(cardSymbolsState.map((s, i) => i === idx ? { ...s, symbol_path: value } : s));
  };
  const handleAddSymbol = () => {
    setCardSymbolsState([...cardSymbolsState, { symbol_index: cardSymbolsState.length + 1, symbol_path: "" }]);
  };
  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post('/api/admin/cards/upload-image', formData);
      setCustomBackground(res.data.fileUrl);
    } catch (err) {
      setError("Failed to upload image");
    }
    setUploading(false);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!deckId || !typeId) {
      setError("Deck and type are required");
      return;
    }
    setUploading(true);
    setError("");
    try {
      // Flatten texts for backend: [{ option_index, language, text }]
      const flatTexts = [];
      texts.forEach((t, idx) => {
        Object.entries(t.translations).forEach(([lang, text]) => {
          if (text && text.trim()) {
            flatTexts.push({ option_index: idx + 1, language: lang, text: text.trim() });
          }
        });
      });
      await onSave(card.card_id, {
        deck_id: parseInt(deckId),
        type_id: parseInt(typeId),
        custom_background: customBackground,
        created_by: user?.user_id,
        texts: flatTexts,
        symbols: cardSymbolsState
      });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to update card");
    }
    setUploading(false);
  };
  // Helper to insert symbol reference at cursor
  const insertSymbolReference = (textIdx, langCode, symbolId) => {
    const inputId = `editcard-text-input-${textIdx}-${langCode}`;
    const input = document.getElementById(inputId);
    if (!input) return;
    const value = input.value;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const ref = `{{symbol:${symbolId}}}`;
    const newValue = value.slice(0, start) + ref + value.slice(end);
    handleTextChange(textIdx, langCode, newValue);
    // Restore cursor position after update
    setTimeout(() => {
      input.focus();
      input.selectionStart = input.selectionEnd = start + ref.length;
    }, 0);
  };
  // Only show symbols of type 'Card type' or 'Symbol' from the symbols prop
  const cardSymbols = symbols.filter(symbol => symbol.symbol_type === 'Card type' || symbol.symbol_type === 'Symbol');
  return (
    <div>
      <form onSubmit={handleSubmit} style={{ marginBottom: "20px", padding: "20px", border: "1px solid var(--border)", background: "var(--surface)", maxWidth: 900, minWidth: 600, borderRadius: "20px" }}>
        <h3>Edit Card</h3>
        <div style={{ marginBottom: 10 }}>
          <label>Deck:</label><br />
          <select value={deckId} onChange={e => setDeckId(e.target.value)} required>
            <option value="">Select deck...</option>
            {decks.sort((a, b) => (a.translations?.en?.name || `Deck ${a.deck_id}`).localeCompare(b.translations?.en?.name || `Deck ${b.deck_id}`)).map(deck => (
              <option key={deck.deck_id} value={deck.deck_id}>{deck.translations?.en?.name || `Deck ${deck.deck_id}`}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label>Type:</label><br />
          <select value={typeId} onChange={e => setTypeId(e.target.value)} required>
            <option value="">Select Type</option>
            {cardTypes.sort((a, b) => (a.translations?.en?.name || a.name || a.type_id).localeCompare(b.translations?.en?.name || b.name || b.type_id)).map(type => (
              <option key={type.type_id} value={type.type_id}>
                {type.translations?.en?.name || type.name || type.type_id}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label>Custom Background (PNG):</label><br />
          <input type="file" accept="image/png" onChange={handleBackgroundUpload} />
          {customBackground && <img src={customBackground} alt="bg" style={{ width: 100, height: 100, objectFit: 'cover', marginLeft: 10 }} />}
        </div>

        <div style={{ marginBottom: 8, fontWeight: 500, color: '#357abd' }}>
          You can insert a symbol reference into any text option using the dropdown next to the input. The symbol will appear as <code>{'{{symbol:ID}}'}</code> in the text.
        </div>
        <div style={{ marginBottom: 10 }}>
          <label>Texts:</label>
          {languages.map(lang => (
            <div key={lang.code} style={{ marginBottom: 10, paddingLeft: 10, borderLeft: '2px solid #eee' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{lang.name} ({lang.code}):</div>
              {texts.map((t, idx) => (
                <div key={idx} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'inline-block', width: 80 }}>Option {idx + 1}:</label>
                  <input
                    type="text"
                    id={`editcard-text-input-${idx}-${lang.code}`}
                    value={t.translations?.[lang.code] || ''}
                    onChange={e => handleTextChange(idx, lang.code, e.target.value)}
                    placeholder={`Text in ${lang.name}`}
                    style={{ width: 350, marginRight: 10 }}
                  />
                  {cardSymbols.length > 0 && (
                    <select
                      style={{ minWidth: 180, fontWeight: 600, background: '#f8f9fa', border: '2px solid #4a90e2', color: '#222', padding: '6px 10px', borderRadius: 6 }}
                      onChange={e => {
                        if (e.target.value) {
                          insertSymbolReference(idx, lang.code, e.target.value);
                          e.target.selectedIndex = 0;
                        }
                      }}
                    >
                      <option value="">Insert Symbol...</option>
                      {cardSymbols.map(symbol => (
                        <option key={symbol.symbol_id} value={symbol.symbol_id}>
                          {symbol.translations?.en?.name || symbol.translations?.en || `Symbol ${symbol.symbol_id}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
          ))}
          <button type="button" onClick={handleAddText}>+</button>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label>Symbols:</label>
          {cardSymbolsState.map((s, idx) => (
            <div key={idx} style={{ marginBottom: 5 }}>
              <input type="text" value={s.symbol_path} onChange={e => handleSymbolChange(idx, e.target.value)} placeholder={`Symbol Path ${idx + 1}`} style={{ width: 300, marginRight: 10 }} />
              <button type="button" onClick={handleAddSymbol}>+</button>
            </div>
          ))}
        </div>
        {error && <div style={{ color: 'red' }}>{error}</div>}
        <button type="submit" disabled={uploading}>Save Card</button>
        <button type="button" onClick={onCancel} style={{ marginLeft: 10 }}>Cancel</button>
      </form>
    </div>
  );
}